/**
 * Preferências do usuário, guardadas no navegador.
 *
 * São escolhas de dispositivo e de comportamento local, que não fazem sentido
 * sincronizar entre máquinas: o microfone bom está num computador só.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ScreenSharePresetId } from '@nexus/shared'
import { DEFAULT_AUDIO_OPTIONS, type AudioProcessingOptions } from '@/lib/audio-processing'

export interface AudioPreferences extends AudioProcessingOptions {
  inputDeviceId?: string
  outputDeviceId?: string
  /** Entra em canais de voz já silenciado. */
  startMuted: boolean
  inputVolume: number
  outputVolume: number
  /**
   * Volume dos sons da bandeja, de 0 a 100.
   *
   * Separado do volume geral porque um efeito curto e alto no meio de uma
   * conversa incomoda muito mais do que a voz das pessoas, e quem quer a
   * brincadeira mais baixa não deveria precisar abaixar a chamada inteira.
   */
  volumeDaBandeja: number
}

export interface VideoPreferences {
  inputDeviceId?: string
  defaultScreenPreset: ScreenSharePresetId
  /** Inclui o áudio do sistema ao compartilhar a tela. */
  shareSystemAudio: boolean
  /**
   * Inverte a própria imagem da câmera, como num espelho.
   *
   * Faz sentido para uma webcam apontada para o rosto: é assim que a pessoa se
   * vê no espelho e é o que parece natural. Deixa de fazer sentido quando a
   * entrada de vídeo é uma placa de captura mostrando um jogo ou outro
   * computador, onde a imagem invertida atrapalha de verdade. Vale só para o
   * que você vê; os outros sempre recebem a imagem como ela é.
   */
  espelharCamera: boolean
}

export interface InterfacePreferences {
  /** Modo compacto aproxima as mensagens, como o modo compacto do Discord. */
  compactMessages: boolean
  showMemberList: boolean
  /** Reproduz um som ao entrar e sair de canais de voz. */
  voiceSounds: boolean
  /** Envia com Enter; desligado, Enter quebra linha e Ctrl+Enter envia. */
  sendOnEnter: boolean
  /** Reduz animações além do que o sistema já pede. */
  reduceMotion: boolean
  fontScale: number
}

interface PreferencesState {
  audio: AudioPreferences
  video: VideoPreferences
  ui: InterfacePreferences
  /**
   * Volume por pessoa, de 0 a 200, indexado pelo id de quem fala.
   *
   * Fica separado do volume geral porque resolve outro problema: um microfone
   * mal configurado do outro lado não deveria obrigar a mexer no volume de todo
   * mundo. Só quem sai do padrão aparece aqui, então o objeto costuma ficar
   * vazio.
   */
  userVolumes: Record<string, number>
  /**
   * Volume da tela de cada pessoa, separado do volume da voz dela.
   *
   * Os dois vinham juntos, e o resultado era que abaixar o jogo ou o vídeo de
   * alguém abaixava também a voz da pessoa, justamente quando ela ia explicar o
   * que está na tela.
   */
  volumesDeTela: Record<string, number>
  /**
   * Emojis usados por último, do mais recente para o mais antigo.
   *
   * Na prática cada pessoa repete um punhado pequeno, e procurar de novo o mesmo
   * de sempre é o que torna uma bandeja de emojis cansativa.
   */
  emojisRecentes: string[]

  setAudio: (patch: Partial<AudioPreferences>) => void
  setVideo: (patch: Partial<VideoPreferences>) => void
  setUi: (patch: Partial<InterfacePreferences>) => void
  setUserVolume: (userId: string, volume: number) => void
  setVolumeDeTela: (userId: string, volume: number) => void
  registrarEmoji: (emoji: string) => void
  reset: () => void
}

const defaults = {
  audio: {
    ...DEFAULT_AUDIO_OPTIONS,
    startMuted: false,
    inputVolume: 100,
    outputVolume: 100,
    volumeDaBandeja: 70,
  } satisfies AudioPreferences,
  video: {
    defaultScreenPreset: 'smooth' as ScreenSharePresetId,
    shareSystemAudio: true,
    espelharCamera: true,
  } satisfies VideoPreferences,
  ui: {
    compactMessages: false,
    showMemberList: true,
    voiceSounds: true,
    sendOnEnter: true,
    reduceMotion: false,
    fontScale: 1,
  } satisfies InterfacePreferences,
  userVolumes: {} as Record<string, number>,
  volumesDeTela: {} as Record<string, number>,
  emojisRecentes: [] as string[],
}

/**
 * Guarda um volume individual.
 *
 * O valor padrão sai do registro em vez de ser gravado: cem por cento é a
 * ausência de ajuste, e salvá-lo encheria o armazenamento de entradas que não
 * mudam nada.
 */
function ajustarVolume(
  atual: Record<string, number>,
  userId: string,
  volume: number,
): Record<string, number> {
  const proximo = { ...atual }
  if (volume === 100) delete proximo[userId]
  else proximo[userId] = Math.min(200, Math.max(0, Math.round(volume)))
  return proximo
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      ...defaults,

      setAudio: (patch) => set((state) => ({ audio: { ...state.audio, ...patch } })),
      setVideo: (patch) => set((state) => ({ video: { ...state.video, ...patch } })),
      setUi: (patch) => set((state) => ({ ui: { ...state.ui, ...patch } })),

      setUserVolume: (userId, volume) =>
        set((state) => ({ userVolumes: ajustarVolume(state.userVolumes, userId, volume) })),

      setVolumeDeTela: (userId, volume) =>
        set((state) => ({ volumesDeTela: ajustarVolume(state.volumesDeTela, userId, volume) })),

      registrarEmoji: (emoji) =>
        set((state) => ({
          // Máximo de dezesseis, que é o que cabe em duas fileiras da bandeja.
          emojisRecentes: [emoji, ...state.emojisRecentes.filter((e) => e !== emoji)].slice(0, 16),
        })),

      reset: () => set(defaults),
    }),
    {
      name: 'nexus.preferences',
      version: 1,
      // Uma preferência nova precisa entrar com o padrão, e não sumir porque o
      // objeto salvo é anterior a ela.
      merge: (persisted, current) => {
        const saved = persisted as Partial<PreferencesState> | undefined
        return {
          ...current,
          audio: { ...current.audio, ...(saved?.audio ?? {}) },
          video: { ...current.video, ...(saved?.video ?? {}) },
          ui: { ...current.ui, ...(saved?.ui ?? {}) },
          userVolumes: saved?.userVolumes ?? {},
          volumesDeTela: saved?.volumesDeTela ?? {},
          emojisRecentes: saved?.emojisRecentes ?? [],
        }
      },
    },
  ),
)
